import * as THREE from 'three/webgpu';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/sketch';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';

/**
 * 圆周草图阵列半径锚点解析器。
 *
 * 职责：
 * 1. 按草图实体语义收集可作为阵列半径端点的真实控制点；
 * 2. 在多实体候选中选择距离阵列中心最近的控制点；
 * 3. 保留实体选择顺序和实体内部控制点顺序，使等距结果稳定可复现。
 *
 * SolidWorks 对齐规则：
 * - 直线只比较起点和终点，不计算线段垂足；
 * - 圆只使用圆心，不使用圆周最近点；
 * - 圆弧只比较起点和终点，不使用圆心、弧中点或弧上最近点。
 */
export class SketchCircularPatternRadiusAnchorResolver {
    /**
     * 解析当前选择对应的阵列半径锚点。
     *
     * @param entities 当前选中的顶层草图实体。
     * @param patternCenterWorld 阵列中心世界坐标。
     * @returns 距离阵列中心最近的语义控制点；没有受支持候选时返回 null。
     */
    public resolve(
        entities: readonly THREE.Object3D[],
        patternCenterWorld: THREE.Vector3,
    ): SketchPointObject | null {
        const candidateMap = new Map<string, SketchPointObject>();
        entities.forEach((entity) => this.collectEntityCandidates(entity, candidateMap));

        let nearestPoint: SketchPointObject | null = null;
        let nearestDistanceSquared = Number.POSITIVE_INFINITY;
        candidateMap.forEach((candidate) => {
            const distanceSquared = candidate.getPosition().distanceToSquared(patternCenterWorld);
            // 严格小于才替换，等距时保留选择顺序及起点优先语义。
            if (distanceSquared < nearestDistanceSquared) {
                nearestDistanceSquared = distanceSquared;
                nearestPoint = candidate;
            }
        });
        return nearestPoint;
    }

    /**
     * 按实体类型递归收集半径候选控制点。
     *
     * 槽口和集合只负责展开内部基础实体；控制点使用 UUID 去重，避免共享端点改变等距裁决顺序。
     * 未单独定义 SolidWorks 半径语义的椭圆、抛物线和样条继续使用其真实控制点候选，
     * 但不再退回旧的算术平均中心。
     *
     * @param entity 当前顶层或递归展开的草图实体。
     * @param candidateMap 按首次出现顺序保存的控制点候选。
     */
    private collectEntityCandidates(
        entity: THREE.Object3D,
        candidateMap: Map<string, SketchPointObject>,
    ): void {
        if (isSketchPointObject(entity)) {
            this.appendCandidate(entity, candidateMap);
            return;
        }
        if (isSketchLineObject(entity)) {
            this.appendCandidate(entity.startPoint, candidateMap);
            this.appendCandidate(entity.endPoint, candidateMap);
            return;
        }
        if (isSketchCircleObject(entity)) {
            this.appendCandidate(entity.getCenterControlPoint(), candidateMap);
            return;
        }
        if (isSketchArcObject(entity)) {
            this.appendCandidate(entity.getStartPoint(), candidateMap);
            this.appendCandidate(entity.getEndPoint(), candidateMap);
            return;
        }
        if (isSketchEllipseObject(entity)) {
            this.appendCandidate(entity.getCenterPoint(), candidateMap);
            this.appendCandidate(entity.getStartPoint(), candidateMap);
            this.appendCandidate(entity.getEndPoint(), candidateMap);
            entity.getQuadrantPoints().forEach((point) => this.appendCandidate(point, candidateMap));
            return;
        }
        if (isSketchParabolaObject(entity)) {
            entity.getControlPoints().forEach((point) => this.appendCandidate(point, candidateMap));
            return;
        }
        if (isSketchSplineObject(entity) || isSketchStyleSplineObject(entity)) {
            entity.getControlPoints().forEach((point) => this.appendCandidate(point, candidateMap));
            return;
        }
        if (isSketchSlotObject(entity)) {
            entity.getEdges().forEach((edge) => this.collectEntityCandidates(edge, candidateMap));
            return;
        }
        if (isSketchCollectionObject(entity)) {
            entity.collectSet.forEach((source) => this.collectEntityCandidates(source, candidateMap));
        }
    }

    /**
     * 按首次出现顺序追加唯一候选点。
     *
     * @param point 待追加的草图控制点。
     * @param candidateMap 候选点映射。
     */
    private appendCandidate(
        point: SketchPointObject,
        candidateMap: Map<string, SketchPointObject>,
    ): void {
        if (!candidateMap.has(point.uuid)) {
            candidateMap.set(point.uuid, point);
        }
    }
}
